/**
 * Black-Scholes closed-form European option prices. Reference values for
 * the phase 7 finite-difference pricer.
 *
 * Source: Black, F. and Scholes, M. (1973). The Pricing of Options and
 * Corporate Liabilities. Journal of Political Economy, 81(3), 637-654.
 * Continuous dividend yield q follows Merton, R. C. (1973). Theory of
 * Rational Option Pricing. Bell Journal of Economics and Management
 * Science, 4(1), 141-183.
 *
 *   d1 = (ln(S/K) + (r - q + sigma^2/2) T) / (sigma sqrt(T))
 *   d2 = d1 - sigma sqrt(T)
 *   C  = S e^(-qT) N(d1) - K e^(-rT) N(d2)
 *   P  = K e^(-rT) N(-d2) - S e^(-qT) N(-d1)
 */

export interface BsInputs {
  spot: number;
  strike: number;
  /** Continuously compounded risk-free rate, per year. */
  rate: number;
  /** Annualized volatility. */
  vol: number;
  /** Time to expiry, years. */
  timeYears: number;
  /** Continuous dividend yield, per year. Default 0. */
  dividendYield?: number;
}

/**
 * Standard normal CDF.
 * Abramowitz, M. and Stegun, I. A. (1964). Handbook of Mathematical
 * Functions, formula 26.2.17. Absolute error below 7.5e-8.
 */
export function normCdf(x: number): number {
  const p = 0.2316419;
  const b1 = 0.31938153;
  const b2 = -0.356563782;
  const b3 = 1.781477937;
  const b4 = -1.821255978;
  const b5 = 1.330274429;
  const ax = Math.abs(x);
  const t = 1 / (1 + p * ax);
  const pdf = Math.exp(-0.5 * ax * ax) / Math.sqrt(2 * Math.PI);
  const poly = t * (b1 + t * (b2 + t * (b3 + t * (b4 + t * b5))));
  const upper = pdf * poly;
  return x >= 0 ? 1 - upper : upper;
}

function d1d2(i: BsInputs): [number, number] {
  const q = i.dividendYield ?? 0;
  const sqrtT = Math.sqrt(i.timeYears);
  const d1 =
    (Math.log(i.spot / i.strike) + (i.rate - q + 0.5 * i.vol * i.vol) * i.timeYears) /
    (i.vol * sqrtT);
  return [d1, d1 - i.vol * sqrtT];
}

function intrinsicAtExpiry(i: BsInputs, isCall: boolean): number | null {
  if (i.timeYears > 0 && i.vol > 0) return null;
  // Degenerate: zero time or zero vol collapses to discounted forward intrinsic.
  const q = i.dividendYield ?? 0;
  const fwdS = i.spot * Math.exp(-q * i.timeYears);
  const pvK = i.strike * Math.exp(-i.rate * i.timeYears);
  return Math.max(isCall ? fwdS - pvK : pvK - fwdS, 0);
}

export function bsCall(i: BsInputs): number {
  const degenerate = intrinsicAtExpiry(i, true);
  if (degenerate !== null) return degenerate;
  const q = i.dividendYield ?? 0;
  const [d1, d2] = d1d2(i);
  return (
    i.spot * Math.exp(-q * i.timeYears) * normCdf(d1) -
    i.strike * Math.exp(-i.rate * i.timeYears) * normCdf(d2)
  );
}

export function bsPut(i: BsInputs): number {
  const degenerate = intrinsicAtExpiry(i, false);
  if (degenerate !== null) return degenerate;
  const q = i.dividendYield ?? 0;
  const [d1, d2] = d1d2(i);
  return (
    i.strike * Math.exp(-i.rate * i.timeYears) * normCdf(-d2) -
    i.spot * Math.exp(-q * i.timeYears) * normCdf(-d1)
  );
}
